// ============================================================================
// lib/checkout/native.ts — wrapper RevenueCat pour l'achat IAP natif (iOS/Android).
// ============================================================================
// Utilisé UNIQUEMENT quand `isNativeApp()` est vrai. L'import du SDK
// `@revenuecat/purchases-capacitor` est DYNAMIQUE : on ne charge le paquet
// que sur natif pour éviter :
//   - une erreur au build web (le SDK a des symboles natifs iOS/Android)
//   - une taille de bundle inutile côté web
//
// Init : `initRevenueCat(userId)` doit être appelé au démarrage sur natif
// (dans `main.tsx`, gate par `isNativeApp()`) avec l'uuid Supabase de l'user
// comme `appUserID` — c'est cette clé qui remonte dans les webhooks RC vers
// l'edge fn `revenuecat-webhook`.
//
// ⚠️ Clés publiques RevenueCat : DIFFÉRENTES par plateforme.
//    VITE_RC_PUBLIC_API_KEY_IOS pour iOS
//    VITE_RC_PUBLIC_API_KEY_ANDROID pour Android
// Ce sont des clés publiques (safe à embarquer), pas des secrets webhook.
// ============================================================================

import type { CheckoutProductId, PurchaseResult } from './types';

let initialized = false;
let currentUserId: string | null = null;

function iosKey(): string | undefined {
  return import.meta.env.VITE_RC_PUBLIC_API_KEY_IOS as string | undefined;
}
function androidKey(): string | undefined {
  return import.meta.env.VITE_RC_PUBLIC_API_KEY_ANDROID as string | undefined;
}

/**
 * Configure le SDK RevenueCat au démarrage (natif seulement).
 * À appeler une fois que l'auth est résolue avec l'uuid Supabase de l'user.
 * Idempotent : ré-appelable après un login/logout pour changer d'appUserID.
 */
export async function initRevenueCat(userId: string): Promise<void> {
  const { Capacitor } = await import('@capacitor/core');
  if (!Capacitor.isNativePlatform()) return;

  const { Purchases, LOG_LEVEL } = await import('@revenuecat/purchases-capacitor');
  const platform = Capacitor.getPlatform();

  const apiKey = platform === 'ios' ? iosKey() : androidKey();
  if (!apiKey) {
    console.warn(`[revenuecat] Missing VITE_RC_PUBLIC_API_KEY_${platform.toUpperCase()}`);
    return;
  }

  if (!initialized) {
    // En dev : logs verbeux. En prod : erreurs seulement.
    await Purchases.setLogLevel({ level: import.meta.env.DEV ? LOG_LEVEL.DEBUG : LOG_LEVEL.ERROR });
    await Purchases.configure({ apiKey, appUserID: userId });
    initialized = true;
    currentUserId = userId;
    return;
  }

  // Déjà init — un changement de compte doit appeler `logIn` pour aligner RC.
  if (currentUserId !== userId) {
    await Purchases.logIn({ appUserID: userId });
    currentUserId = userId;
  }
}

/** Vrai si le SDK RC a été correctement configuré au démarrage. */
export function isRevenueCatReady(): boolean {
  return initialized;
}

/**
 * Achète un product par son ID interne (mappé vers un package RC via l'offering
 * courante). L'utilisateur voit la feuille système Apple / Google standard.
 * Le webhook RC → `revenuecat-webhook` écrit dans `subscriptions` en async.
 * La promesse résout dès que la feuille système se ferme (avant le webhook).
 */
export async function purchaseNative(productId: CheckoutProductId): Promise<PurchaseResult> {
  if (!initialized) {
    return { status: 'error', message: 'revenuecat_not_ready' };
  }
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const offerings = await Purchases.getOfferings();
    const current = offerings.current;
    if (!current) return { status: 'error', message: 'no_offering' };

    // On cherche le package RC dont le product_identifier matche notre ID interne.
    const pkg = current.availablePackages.find((p) => p.product.identifier === productId);
    if (!pkg) return { status: 'error', message: `product_not_available:${productId}` };

    const result = await Purchases.purchasePackage({ aPackage: pkg });
    // Achat réussi si l'entitlement student_access est actif.
    const entitlements = result.customerInfo?.entitlements?.active ?? {};
    if (Object.keys(entitlements).length > 0) {
      return { status: 'success' };
    }
    // Ask to Buy (parental control iOS) → l'achat est pending.
    return { status: 'pending' };
  } catch (err) {
    // deno-lint-ignore no-explicit-any
    const e = err as any;
    if (e?.code === 'PURCHASE_CANCELLED_ERROR' || /cancel/i.test(String(e?.message))) {
      return { status: 'user_cancelled' };
    }
    console.error('[revenuecat] purchase failed', err);
    return { status: 'error', message: String(e?.message ?? e ?? 'unknown') };
  }
}

/**
 * Ouvre l'écran de GESTION des abonnements du store (Apple / Google). Sur iOS
 * un abonnement IAP se résilie / se change UNIQUEMENT via les Réglages Apple —
 * jamais via le portail Stripe (qui ne connaît pas les subs IAP). Guideline
 * Apple : on a le droit de renvoyer vers cet écran système.
 */
export async function openManageSubscriptionsNative(): Promise<void> {
  const { Capacitor } = await import('@capacitor/core');
  const platform = Capacitor.getPlatform();
  const url =
    platform === 'android'
      ? 'https://play.google.com/store/account/subscriptions'
      : 'https://apps.apple.com/account/subscriptions';
  const { Browser } = await import('@capacitor/browser');
  await Browser.open({ url });
}

/**
 * Restaure les achats précédents (App Store : « Restore purchases » standard).
 * Nécessaire pour Apple review (Guideline 3.1.1 : les Restore doivent être
 * accessibles depuis l'app), utile pour un user qui change de device.
 */
export async function restoreNative(): Promise<PurchaseResult> {
  if (!initialized) return { status: 'error', message: 'revenuecat_not_ready' };
  try {
    const { Purchases } = await import('@revenuecat/purchases-capacitor');
    const result = await Purchases.restorePurchases();
    const entitlements = result.customerInfo?.entitlements?.active ?? {};
    return Object.keys(entitlements).length > 0
      ? { status: 'success' }
      : { status: 'error', message: 'no_active_entitlement' };
  } catch (err) {
    // deno-lint-ignore no-explicit-any
    return { status: 'error', message: String((err as any)?.message ?? 'restore_failed') };
  }
}
